import {
	DropOverActionList,
	IconButton
} from "client/Components";
import { actionComponentFactory, computeActionsToRender } from "client/Components/ActionList/actionsRenderHelpers";
import { FolderCounters, MessageReadState } from "client/Api/Email/email";
import {
	Fragment,
	ReactElement,
	RefObject,
	useMemo,
	useRef,
	useState
} from "react";
import {
	EmailListAction,
	EmailListActionContext,
	EmailListActions
} from "../../Actions/EmailListActions";
import { generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ActionAndOthers } from "client_legacy/ndaw/actions";

export type MoreOptionsProps = {
	smtpAddress: string;
	folderId: string;
	counters: FolderCounters;
	isMobile: boolean;
	onRefreshList(): void;
	onFolderReadStatusChange(
		smtpAddress: string,
		folderId: string,
		status: MessageReadState
	): void;
};

export const MoreOptions = (props: MoreOptionsProps): ReactElement => {
	const {
		smtpAddress,
		folderId,
		counters,
		isMobile,
		onRefreshList,
		onFolderReadStatusChange
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const listActions = useMemo(
		(): readonly EmailListAction[] => {
			return new EmailListActions({
				handlers: {
					refreshList: onRefreshList,
					markAllAsRead: (smtpAddress: string, folderId: string) =>
						onFolderReadStatusChange(
							smtpAddress,
							folderId,
							MessageReadState.Read
						),
					markAllAsUnRead: (smtpAddress: string, folderId: string) =>
						onFolderReadStatusChange(
							smtpAddress,
							folderId,
							MessageReadState.Unread
						)
				}
			})
				.getAllActions();
		},
		[
			onFolderReadStatusChange,
			onRefreshList
		]
	);

	const context = useMemo(
		(): EmailListActionContext => ({
			smtpAddress,
			folderId,
			counters
		}),
		[
			smtpAddress,
			counters,
			folderId
		]
	);

	const availableListActions: readonly ActionAndOthers<EmailListActionContext>[] = computeActionsToRender(listActions, context);
	const trackingId: string = "email-list-more-options";

	return (
		<>
			<IconButton
				kind="transparent"
				icon="ellipsis"
				onClick={() => setIsOpen(status => !status)}
				ref={ref}
				trackingId={trackingId}
				shape="round"
				isActive={isOpen}
				size={isMobile ? "large" : "normal"}
			/>
			{isOpen && ref.current && (
				<DropOverActionList
					target={ref.current}
					targetAlignment="bl"
					contentAlignment="tl"
					onClose={() => setIsOpen(false)}
					trackingId={generateTrackingId(trackingId, "__dropover")}
					menuTitle={localize("_EmailList")}
				>
					{availableListActions.map((x) => (
						<Fragment key={x.name}>
							{actionComponentFactory(x, context)}
						</Fragment>
					))}
				</DropOverActionList>
			)}
		</>
	);
};
MoreOptions.displayName = "MoreOptions";